import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import employeeCategoryService from "../../services/EmployeeCategoryService";

// Normalize one raw category from the API into the shape the UI expect
const normalizeCategory = (c) => ({
  id: c.HEC_EMPLOYMENT_CATEGORY_ID ?? c.id ?? null,
  code: c.HEC_CATEGORY_CODE ?? c.code ?? "",
  name: c.HEC_CATEGORY_NAME ?? c.name ?? "",
  isActive: c.HEC_IS_ACTIVE ?? c.isActive ?? true,
  remarks: c.HEC_REMARKS ?? c.remarks ?? "",
  createdBy: c.HEC_CREATED_BY ?? null,
  createdDate: c.HEC_CREATED_DATE ?? null,
  updatedBy: c.HEC_UPDATED_BY ?? null,
  updatedDate: c.HEC_UPDATED_DATE ?? null,
  raw: c,
});

// Extract the array from any of the shapes the backend may return
const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.ResultSet)) return data.ResultSet;
  if (Array.isArray(data?.resultSet)) return data.resultSet;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const getEmployeeCategoryList = createAsyncThunk(
  "employeeCategory/getEmployeeCategoryList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeCategoryService.GetEmployeeCategoryList();
      const list = extractList(response.data);
      return list.map(normalizeCategory);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
          error.message ||
          "Failed to get employee category list"
      );
    }
  }
);

export const addEmployeeCategory = createAsyncThunk(
  "employeeCategory/addEmployeeCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await employeeCategoryService.AddEmployeeCategory(payload);
      return extractList(response.data).map(normalizeCategory);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
          error.message ||
          "Failed to add employee category"
      );
    }
  }
);

export const updateEmployeeCategory = createAsyncThunk(
  "employeeCategory/updateEmployeeCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await employeeCategoryService.UpdateEmployeeCategory(payload);
      return extractList(response.data).map(normalizeCategory);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
          error.message ||
          "Failed to update employee category"
      );
    }
  }
);

const initialState = {
  categories: [],
  loading: false,
  saving: false,
  error: null,
  success: false,
};

const employeeCategorySlice = createSlice({
  name: "employeeCategory",
  initialState,
  reducers: {
    clearEmployeeCategoryError: (state) => {
      state.error = null;
    },
    clearEmployeeCategorySuccess: (state) => {
      state.success = false;
    },
    clearEmployeeCategories: (state) => {
      state.categories = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeCategoryList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmployeeCategoryList.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(getEmployeeCategoryList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load employee categories";
      })

      .addCase(addEmployeeCategory.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addEmployeeCategory.fulfilled, (state) => {
        state.saving = false;
        state.success = true;
      })
      .addCase(addEmployeeCategory.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to add employee category";
      })

      .addCase(updateEmployeeCategory.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateEmployeeCategory.fulfilled, (state) => {
        state.saving = false;
        state.success = true;
      })
      .addCase(updateEmployeeCategory.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to update employee category";
      });
  },
});

export const {
  clearEmployeeCategoryError,
  clearEmployeeCategorySuccess,
  clearEmployeeCategories,
} = employeeCategorySlice.actions;

export const selectEmployeeCategories = (state) =>
  state.employeeCategorySlice.categories;

// Only active categories are useful in dropdowns.
export const selectActiveEmployeeCategories = (state) =>
  state.employeeCategorySlice.categories.filter((c) => c.isActive !== false);

export const selectEmployeeCategoryLoading = (state) =>
  state.employeeCategorySlice.loading;

export default employeeCategorySlice.reducer;